import { useCallback, useMemo } from 'react';
import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import { Panel } from '../Panel.tsx';
import { Empty } from '../Empty.tsx';
import { NO_VALUE, unitIsTight } from '../format.ts';
import { levelTone } from '../state.ts';
import { Icon } from '../../shell/Icon.tsx';
import { ticks } from '../timeScale.ts';
import type { Channel, TimeAxis } from './channels.ts';
import { CursorOverlay } from './CursorOverlay.tsx';
import { useCursor } from './cursor.ts';
import {
  frameOf, limitCallouts, MIN_PITCH, placeCallouts,
  type Callout, type Frame, type Limit, type LimitCallout,
} from './frame.ts';
import { GutterCallout } from './GutterCallout.tsx';
import { usePlotSurface, xScale } from './plotSurface.ts';
import {
  buildSegments, channelBox, quoteAt, samplesIn, yTicks, type Samples,
} from './scale.ts';
import { useMeasure } from './useMeasure.ts';
import styles from './charts.module.css';

/* ==========================================================================
   One channel, one card, one lane per robot.

   `formant-fleet-observability-dashboard.png`'s CPU panel is the reference:
   two devices in ONE card as two named lanes stacked on a shared x-axis,
   never overplotted — because two traces of the same quantity from two
   machines cross constantly and the crossing means nothing.

   What the card publishes, so an operator never has to supply it from
   memory (§13 tell 14, tell 15):

     · the y-box it drew in, printed at both ends of the axis and on every
       interior grid line the plot is tall enough to label
     · whether that box is the metric's real domain or was fitted to this
       window (a fitted box says so)
     · every threshold the channel is judged on, as a rule with its value at
       the right edge, grey until it is actually crossed in this window
     · the value at the cursor: in the panel's own title bar when the card
       holds one robot, and inside each lane when it holds several
     · the unit, once, as a dim suffix on that value

   One robot is the common case and it does not get an identity strip: the
   surface already names its subject in the crumb and in the rail, and nine
   cards each repeating `jetrover-01` is nine lines of type that carry no
   information and thirteen pixels of plot that do.
   ========================================================================== */

export interface ChartSeries {
  /** robot_id — the machine string, set in mono wherever it appears. */
  id: string;
  colour: string;
  readings: readonly TelemetryReading[];
}

/** Height of the identity strip above each lane, when there is more than one. */
const ID_H = 13;
const MIN_PLOT_H = 26;
/** Clearance under a lane's plot, so a floor value is not drawn on the
    hairline that ends the lane. Matches the signal stack's BOT_PAD. */
const LANE_BOT = 3;
/** Below this a plot cannot carry labels on its interior grid lines. */
const INTERIOR_LABEL_H = 62;

function fmt(v: number, digits: number): string {
  return v.toFixed(digits);
}

/**
 * Shortest label that still distinguishes the tick from its neighbours, and
 * never more precise than the quantity is: a satellite count has no decimal,
 * whatever the span works out to.
 */
function tickLabel(v: number, span: number, max: number): string {
  const digits = span >= 20 ? 0 : span >= 2 ? 1 : 2;
  return v.toFixed(Math.min(digits, max));
}

export function TimeSeriesCard({
  channel,
  series,
  axis,
  minHeight,
  expanded,
  onExpand,
}: {
  channel: Channel;
  series: readonly ChartSeries[];
  axis: TimeAxis;
  /** Floor for the card, so a grid row cannot squeeze a lane to nothing. */
  minHeight?: number;
  /** This card currently has the grid to itself. */
  expanded?: boolean | undefined;
  /** Give the card the whole grid, or hand it back. Omit for no affordance. */
  onExpand?: ((key: string, next: boolean) => void) | undefined;
}) {
  const cursor = useCursor();
  const [bodyRef, box] = useMeasure<HTMLDivElement>();

  const samples = useMemo(
    () => series.map((s) => samplesIn(s.readings, channel, cursor.from, cursor.to, axis)),
    [series, channel, cursor.from, cursor.to, axis],
  );

  const cadence = samples.find((s) => s.cadence > 0)?.cadence ?? 60_000;
  const frame = useMemo(() => frameOf(Math.max(1, box.w)), [box.w]);
  const surface = usePlotSurface({
    stepMs: cadence,
    padL: frame.padL,
    padR: frame.padR,
  });

  /* Composed refs have to be stable. React re-invokes an inline ref callback
     on every render — with `null` first — so an unstable one tears the resize
     observer down and re-measures synchronously each pass, which is how a
     measured plot ends up in an update loop. */
  const { setBox } = surface;
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      bodyRef(el);
      setBox(el);
    },
    [bodyRef, setBox],
  );

  /* One box for every lane in the card: the whole point of stacking robots is
     that their traces are comparable, and two lanes on two scales are not. */
  const merged: Samples = useMemo(() => {
    let lo: number | null = null;
    let hi: number | null = null;
    for (const s of samples) {
      if (s.lo !== null) lo = lo === null ? s.lo : Math.min(lo, s.lo);
      if (s.hi !== null) hi = hi === null ? s.hi : Math.max(hi, s.hi);
    }
    return { ...(samples[0] ?? ({} as Samples)), lo, hi };
  }, [samples]);
  const plotBox = useMemo(() => channelBox(channel, merged), [channel, merged]);

  const total = samples.reduce((n, s) => n + s.count, 0);
  const lanes = series.length;
  const idH = lanes > 1 ? ID_H : 0;
  const laneH = lanes > 0 ? Math.max(idH + MIN_PLOT_H, box.h / lanes) : 0;
  const plotH = Math.max(MIN_PLOT_H, laneH - idH);
  /* A LANE THE CARD CANNOT FIT IS SCROLLED TO, NOT CUT OFF.

     Below the floor the lanes stop shrinking — 26px is where a trace stops
     being a trace — so six robots need more height than a four-column grid
     cell has. Drawing the SVG at the body's height instead simply clipped
     the last two robots at the card's edge with nothing saying they existed,
     which is the worst available outcome: a comparison that silently drops
     comparands. The workspace sizes its grid rows off the comparand count so
     this should never bind (see `cardMin`); the scroll is the guarantee that
     a lane is never lost if it does. */
  const svgH = Math.max(1, box.h, lanes * laneH);
  const w = box.w;
  const x = xScale(cursor.from, cursor.to, frame.plotW);
  /* A 6h window at two ticks is `09:00 12:00`, and three hours between
     gridlines is an axis the eye cannot interpolate on. Anything with 150px
     of plot gets a third tick — at 198px (a four-column card at 1600) that is
     a 2h ladder with 66px between labels, which is still twice a `08:00`'s
     own width. Narrower than that and the labels would touch, so the floor
     drops back to two. */
  const xt = ticks(
    cursor.from,
    cursor.to,
    Math.max(frame.plotW >= 150 ? 3 : 2, Math.round(frame.plotW / 110)),
  );

  /* Only limits this channel is entitled to DRAW. Disk free is judged on disk
     used, so a rule in GiB would be a number the contract never published. */
  const drawable = channel.judge === undefined;
  const limits: readonly Limit[] = drawable
    ? ([
        channel.warn !== undefined ? { v: channel.warn, kind: 'warn' as const } : null,
        channel.bad !== undefined ? { v: channel.bad, kind: 'bad' as const } : null,
      ].filter((l): l is Limit => l !== null))
    : [];

  const crossed = useMemo(() => {
    const out = { warn: false, bad: false };
    for (const s of samples) {
      for (const l of s.lv) {
        if (l === 'warn') out.warn = true;
        if (l === 'bad') { out.warn = true; out.bad = true; }
      }
    }
    return out;
  }, [samples]);

  /* THE BOX IS STATED ONCE, IN THE PANEL BAR.

     Every lane in the card shares one y-box — that is what makes the lanes
     comparable — so printing its two ends inside each of three 23px lanes is
     six numbers saying one thing, laid over the traces they are supposed to
     be read against. The bar carries it instead, in the same grammar the
     signal stack's lane captions use: `29.3 – 49.5 °C of 0 – 90`. */
  const rangeLabel =
    merged.lo === null || merged.hi === null
      ? NO_VALUE
      : `${fmt(plotBox.lo, channel.digits)} – ${fmt(plotBox.hi, channel.digits)}`;
  const domainLabel =
    plotBox.fitted && channel.min !== undefined && channel.max !== undefined
      ? `of ${channel.min} – ${channel.max}`
      : null;

  /* A single-lane card quotes the cursor in its own title bar, the way a
     Formant tile captions itself — so the number tracking the playhead is on
     the same line as the name of the thing it belongs to. */
  const head = lanes === 1 ? samples[0]! : null;
  /* Same rule as everywhere else in the kit: a cursor asks about an instant
     and gets that instant or nothing; no cursor asks "what is it now" and
     gets the newest reading, whose age the surface states once. */
  const headQ = head === null ? { i: -1, stale: false } : quoteAt(head, cursor.at, cursor.to);
  const headIdx = headQ.i;
  const headVal = headIdx >= 0 ? head!.v[headIdx] : null;
  const headTone = headIdx >= 0 ? levelTone(head!.lv[headIdx]!) : 'neutral';
  const pad = unitIsTight(channel.unit) ? '' : ' ';

  return (
    <Panel
      title={channel.label}
      {...(lanes > 1 ? { count: lanes } : {})}
      meta={
        <span className={styles.cardMeta} title={channel.hint}>
          <span className={styles.metaRange}>{rangeLabel}</span>
          <span className={styles.metaUnit}>{channel.unit}</span>
          {domainLabel !== null && (
            <span className={styles.metaFitted}>{domainLabel}</span>
          )}
          {/* Panel's meta never shrinks, so a long one squeezes the title out
              of its own bar. In a comparison card the judge note gives way:
              it is the least load-bearing of the three facts, and the rail's
              dot and the signal stack's caption both still carry it. */}
          {channel.judgeNote !== undefined && lanes === 1 && (
            <span className={styles.metaFitted}>{`judged on ${channel.judgeNote}`}</span>
          )}
          {lanes === 1 && (
            <span className={styles.metaValue} data-tone={headVal == null ? 'neutral' : headTone}>
              {headVal == null ? NO_VALUE : headVal.toFixed(channel.digits)}
              <span className={styles.metaUnit}>{`${pad}${channel.unit}`}</span>
            </span>
          )}
        </span>
      }
      tools={
        onExpand === undefined ? undefined : (
          <button
            type="button"
            className={styles.panelBtn}
            data-on={expanded === true}
            aria-pressed={expanded === true}
            title={
              expanded === true
                ? `Return ${channel.label} to the grid`
                : `Give ${channel.label} the whole grid — the window and the cursor do not change`
            }
            aria-label={
              expanded === true ? `Collapse ${channel.label}` : `Expand ${channel.label}`
            }
            onClick={() => onExpand(channel.key, expanded !== true)}
          >
            <Icon name={expanded === true ? 'collapse' : 'expand'} size={13} />
          </button>
        )
      }
      pad="none"
      scroll={false}
      className={styles.card}
    >
      {total === 0 ? (
        <Empty detail={`${channel.label.toLowerCase()} · no readings in window`}>
          Nothing arrived in this window
        </Empty>
      ) : (
        <>
          <div
            className={styles.plotBody}
            style={minHeight === undefined ? undefined : { minHeight: `${minHeight}px` }}
            ref={attach}
            role="group"
            aria-label={`${channel.label} in ${channel.unit}, ${lanes} ${
              lanes === 1 ? 'robot' : 'robots'
            }, ${rangeLabel} in window. Arrow keys move the cursor.`}
            tabIndex={0}
            {...surface.handlers}
          >
            {w > 0 && (
              <svg width={w} height={svgH} aria-hidden="true">
                {/* The plot's two vertical edges: the gutters are chrome and
                    the box between them is the picture. */}
                <line
                  x1={frame.x0 + 0.5} x2={frame.x0 + 0.5} y1={0} y2={svgH}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />
                <line
                  x1={frame.x1 + 0.5} x2={frame.x1 + 0.5} y1={0} y2={svgH}
                  stroke="var(--line-2)" strokeWidth={1} shapeRendering="crispEdges"
                />
                {series.map((s, i) => (
                  <Lane
                    key={s.id}
                    top={i * laneH}
                    idH={idH}
                    plotH={plotH}
                    frame={frame}
                    channel={channel}
                    series={s}
                    samples={samples[i]!}
                    box={plotBox}
                    limits={limits}
                    crossed={crossed}
                    x={x}
                    xTicks={xt.map((t) => t.f * frame.plotW)}
                    at={cursor.at}
                    edge={cursor.to}
                    lanes={lanes}
                    bottom={svgH - 3}
                  />
                ))}
                <g transform={`translate(${frame.x0},0)`}>
                  <CursorOverlay
                    from={cursor.from}
                    to={cursor.to}
                    width={frame.plotW}
                    height={svgH}
                    at={cursor.at}
                    playhead={cursor.playhead}
                    brush={surface.brush}
                  />
                </g>
              </svg>
            )}
          </div>
          <div className={styles.ruler}>
            {xt.map((t) => (
              <span
                key={t.t}
                className={styles.rulerTick}
                style={{ left: `${frame.x0 + t.f * frame.plotW}px` }}
              >
                {t.label}
              </span>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   One robot's band inside the card.
   -------------------------------------------------------------------------- */

function Lane({
  top, idH, plotH, frame, channel, series, samples, box, limits, crossed,
  x, xTicks, at, edge, lanes, bottom,
}: {
  top: number;
  idH: number;
  plotH: number;
  frame: Frame;
  channel: Channel;
  series: ChartSeries;
  samples: Samples;
  box: { lo: number; hi: number; fitted: boolean };
  limits: readonly Limit[];
  crossed: { warn: boolean; bad: boolean };
  x: (t: number) => number;
  xTicks: readonly number[];
  /** The cursor's instant, or null when nobody is pointing at the plot. */
  at: number | null;
  /** The window's right edge — what a quoted sample's age is measured from. */
  edge: number;
  lanes: number;
  /** The last baseline inside the panel — nothing may be drawn past it. */
  bottom: number;
}) {
  const width = frame.plotW;
  const y0 = top + idH;
  const span = box.hi - box.lo || 1;
  /* THE FLOOR IS NOT THE SEPARATOR. A lane whose plot runs to its own last
     pixel draws a value sitting AT the domain's floor — an idle robot's
     0.00 m/s, a robot with no satellites — as a 1.4px stroke centred on the
     hairline that separates it from the next lane, and the reading stops
     being distinguishable from the chrome. The signal stack has always kept
     4px of clearance here (BOT_PAD); the card lane now keeps 3. */
  const h = Math.max(10, plotH - LANE_BOT);
  const y = (v: number) => y0 + h - ((v - box.lo) / span) * h;

  const trace = useMemo(
    () => buildSegments(samples, x, y, channel.wrap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, box.lo, box.hi, plotH, y0, width, channel.wrap],
  );

  const { i, stale } = quoteAt(samples, at, edge);
  const sparse = samples.t.length > 1 && width / (samples.t.length - 1) >= 10;
  const cur = i >= 0 ? samples.v[i] : null;
  const curLevel = i >= 0 ? samples.lv[i]! : 'unknown';
  /* The card's y-box is one box for every lane in it — that is what makes the
     lanes comparable — so its two ends, and the limit labels, are printed on
     the top lane only. Three copies of one axis is three lanes of clutter on
     the 30px lanes of a four-column grid, where the number is one lane away
     and the gridlines carry the reading down. */
  const first = top === 0;

  /* THE READING AND THE RULE LABELS SHARE THE RIGHT GUTTER, exactly as they
     do in the signal stack — same two columns, same placement rule, same
     stub-and-leader callout (see frame.ts and GutterCallout.tsx).

     Both are POSITIONED. A three-robot Battery card used to print five
     numbers in one run — 79.9 / 40 / 15 / 7.9 / 12.1 — of which only the two
     limits meant anything by where they sat, because each lane's reading was
     pinned to the top of its own lane. Each reading is now a callout on its
     own trace's terminal point, in its own robot's colour, so the column
     reads as five heights rather than as a list.

     THE LANE'S FLOOR IS ITS SEPARATOR, NOT ITS PLOT'S LAST PIXEL. `y0 +
     plotH` is exactly where the NEXT lane draws its hairline, so a label
     allowed to sit there has that hairline struck through its glyphs — which
     is what `−85 dBm` looked like on every three-robot Link and GNSS card.
     Four pixels of clearance puts the whole 10px glyph box above the rule;
     where that leaves no room, the placement pulls the stack up from the
     floor rather than dropping the error limit. */
  const valueAnchor = cur == null ? null : y(cur);
  /* The limits are LABELLED once per card, on the top lane, because every
     lane shares one y-box (see below) — but the reading is per-lane, so a
     lower lane lays out its own reading alone. */
  const ceiling = top + (lanes > 1 ? 8 : 4);
  const floor = Math.min(y0 + h - 4, bottom);
  const placed = placeCallouts<Callout | LimitCallout>(
    [
      ...(lanes > 1
        ? [{ key: 'value', kind: 'value' as const, anchorY: valueAnchor ?? -Infinity, rank: 0 }]
        : []),
      ...(first ? limitCallouts(limits, y, box) : []),
    ],
    ceiling,
    floor,
  );
  const valueTag = placed.find((c) => c.kind === 'value');
  const rules = placed.filter((c): c is LimitCallout & { labelY: number | null } =>
    c.kind !== 'value');
  /* Every lane draws every rule — each robot has to be readable against its
     own limit — whether or not this lane carries the writing. */
  const ruleYs = limitCallouts(limits, y, box);

  /* A grid line under a threshold rule is two horizontal lines at one value.
     The rule wins: it is the line that means something. */
  const gridY = (channel.ticks ?? yTicks(box.lo, box.hi, h >= 110 ? 4 : 2))
    .filter((v) => v > box.lo && v < box.hi)
    .filter((v) => !ruleYs.some((l) => Math.abs(l.anchorY - y(v)) < 2));
  const printTick = (v: number) =>
    channel.tickText === undefined
      ? tickLabel(v, span, channel.digits)
      : channel.tickText(v, frame.tight);

  const colourFor = (lv: 'ok' | 'warn' | 'bad' | 'unknown') =>
    lv === 'bad' ? 'var(--error)' : lv === 'warn' ? 'var(--warn)' : series.colour;

  const axisTopY = y0 + 8;
  const axisBotY = y0 + h - 1;
  /* Same rule as the signal stack: the box's ends are the axis and an
     interior tick's LABEL is dropped where it cannot clear them, or its own
     neighbour, by one pitch. The gridline stays either way. */
  const tickLabels: { v: number; gy: number }[] = [];
  for (const v of gridY) {
    const gy = y(v) + 3;
    if (gy < axisTopY + MIN_PITCH || gy > axisBotY - MIN_PITCH) continue;
    const prev = tickLabels[tickLabels.length - 1];
    if (prev !== undefined && Math.abs(gy - prev.gy) < MIN_PITCH) continue;
    tickLabels.push({ v, gy });
  }
  /* ONCE A LANE IS A PLOT IN ITS OWN RIGHT, IT CARRIES ITS OWN AXIS.

     An expanded card gives each robot 110px, and at that height "carry the
     number down from the lane above" stops being a glance and starts being
     work — the reference frames that stack lanes this tall
     (`auterion-flight-log-review.png`) label every one of them. Below this
     the ends stay on the top lane, where they cost one row instead of
     three. */
  /* …and only where the two ends are a pitch apart. A six-robot card gives
     each lane twenty pixels, and two axis numbers eight pixels apart are not
     an axis, they are a smudge — the card's own header already carries
     `10.6 – 94.5 % of 0 – 100`, which is the same fact set in a place that
     has room for it. */
  const ownAxis = (first || h >= 90) && axisBotY - axisTopY >= MIN_PITCH;

  return (
    <g>
      {/* lane separation is a hairline, not a gap */}
      {top > 0 && (
        <line
          x1={0} x2={frame.width} y1={top} y2={top}
          stroke="var(--line)" strokeWidth={1} shapeRendering="crispEdges"
        />
      )}

      {/* ---- the left gutter: identity, then the axis ------------------ */}
      {lanes > 1 && (
        <>
          <rect x={2} y={top + 4} width={7} height={2} fill={series.colour} />
          <text className={styles.gutterName} x={frame.nameX} y={top + 10}>
            {series.id}
          </text>
        </>
      )}
      {ownAxis && (
        <>
          <line
            x1={frame.x0 - 3} x2={frame.x0} y1={axisTopY - 3} y2={axisTopY - 3}
            stroke="var(--line-3)" strokeWidth={1} shapeRendering="crispEdges"
          />
          <line
            x1={frame.x0 - 3} x2={frame.x0} y1={axisBotY - 3} y2={axisBotY - 3}
            stroke="var(--line-3)" strokeWidth={1} shapeRendering="crispEdges"
          />
          {/* THE ENDS ARE BARE NUMBERS. The unit is on the range line
              directly below, and an axis end that carries one sits level
              with the lane's name and reads as a reading of it — `Speed
              0.50 m/s` is a sentence the plot never said. The stub and the
              column say it is an axis; the numbers say where. */}
          <text className={styles.gutterAxis} x={frame.axisX} y={axisTopY} textAnchor="end">
            {printTick(box.hi)}
          </text>
          <text className={styles.gutterAxis} x={frame.axisX} y={axisBotY} textAnchor="end">
            {printTick(box.lo)}
          </text>
          {h >= INTERIOR_LABEL_H &&
            tickLabels.map(({ v, gy }) => (
              <text
                key={`gl${v}`}
                className={styles.gutterTick}
                x={frame.axisX}
                y={gy}
                textAnchor="end"
              >
                {printTick(v)}
              </text>
            ))}
        </>
      )}

      {/* ---- the right gutter: the reading, then one label per rule ---- */}
      {/* THE READING IS A CALLOUT ON ITS OWN TRACE, in its own robot's
          colour — so on a Battery card comparing three machines the column
          reads as three heights and two rules, not as five numbers in a
          run. */}
      {valueTag !== undefined && (
        <GutterCallout
          frame={frame}
          anchorY={valueAnchor}
          labelY={valueTag.labelY}
          stroke={colourFor(curLevel)}
          variant="reading"
          strokeOpacity={stale ? 0.5 : 1}
          value={cur == null ? NO_VALUE : cur.toFixed(channel.digits)}
          unit={cur == null ? null : channel.unit}
          valueProps={{
            'data-tone': cur == null ? 'neutral' : levelTone(curLevel),
            'data-stale': stale,
          }}
          unitProps={{ 'data-stale': stale }}
        />
      )}
      {/* THE LIMITS ARE LABELLED ONCE PER CARD, NOT ONCE PER LANE.

          Every lane in the card shares one y-box — that is the whole point of
          stacking robots — so a rule sits at the same height in all of them
          and three copies of `40 %` / `15 %` are one fact typeset three
          times. On a three-robot comparison over eight channels that was
          forty-eight coloured numbers, which is most of §3's whole saturation
          budget spent restating a constant. The RULES stay in every lane —
          each robot has to be readable against its own limit — and the top
          lane carries the writing, beside the axis ends it already carries. */}
      {rules.map((l) => (
        <g key={l.kind} data-limit={l.kind}>
          <GutterCallout
            frame={frame}
            anchorY={l.anchorY}
            labelY={l.labelY}
            stroke={l.kind === 'bad' ? 'var(--error)' : 'var(--warn)'}
            variant="limit"
            value={String(l.v)}
            unit={channel.unit}
            valueProps={{ 'data-kind': l.kind }}
            unitProps={{ 'data-kind': l.kind }}
          />
        </g>
      ))}

      {/* ---- the plot -------------------------------------------------- */}
      <g transform={`translate(${frame.x0},0)`}>
        {gridY.map((v) => (
          <line
            key={`g${v}`}
            x1={0} x2={width} y1={y(v)} y2={y(v)}
            stroke="var(--plot-grid)" strokeWidth={1} shapeRendering="crispEdges"
          />
        ))}
        {xTicks.map((px) => (
          <line
            key={`x${px}`}
            x1={px} x2={px} y1={y0} y2={y0 + h}
            stroke="var(--plot-grid)" strokeWidth={1} shapeRendering="crispEdges"
          />
        ))}

        {ruleYs.map((l) => (
          <line
            key={l.kind}
            x1={0} x2={width} y1={l.anchorY} y2={l.anchorY}
            stroke={l.kind === 'bad' ? 'var(--error)' : 'var(--warn)'}
            strokeOpacity={crossed[l.kind] ? 0.9 : 0.4}
            strokeWidth={1}
            strokeDasharray="1 3"
            shapeRendering="crispEdges"
          />
        ))}

        {trace.segments.map((s, n) => (
          <path
            key={`${n}-${s.d.length}`}
            d={s.d}
            fill="none"
            stroke={colourFor(s.level)}
            strokeWidth={1.4}
            strokeLinecap="butt"
            strokeLinejoin="round"
          />
        ))}

        {/* Samples a break left on their own — a wrapping heading loses half
            its measurements to the seam without these. */}
        {trace.points.map((p, n) => (
          <circle key={`p${n}`} cx={p.x} cy={p.y} r={1.4} fill={colourFor(p.level)} />
        ))}

        {/* The samples themselves, once they are far enough apart to resolve —
            so a sparse cadence reads as measurements rather than as a signal. */}
        {sparse &&
          samples.t.map((ts, k) =>
            samples.v[k] == null ? null : (
              <circle
                key={ts}
                cx={x(ts)}
                cy={y(samples.v[k] as number)}
                r={1.6}
                fill={colourFor(samples.lv[k]!)}
              />
            ),
          )}

        {i >= 0 && cur != null && (at !== null || stale) && (
          <circle
            cx={x(samples.t[i]!)}
            cy={y(cur)}
            r={2.5}
            fill={colourFor(curLevel)}
            stroke="var(--bg-1)"
            strokeWidth={1}
          />
        )}
      </g>
    </g>
  );
}
